import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { GetMainRepresentationByArticleIdLayerLoadMode } from 'api/models';
import { useGetMainRepresentationByArticleIdHook } from 'api/queries/articles/articles';
import { getGetProjectsQueryKey, useSaveProject } from 'api/queries/projects/projects';
import {
    AUTOSAVE_DELAY_MS,
    AUTOSAVE_ENABLED_KEY
} from 'app/modules/wallforming/components/projects-manager/constants';
import { PendingLoadedProject } from 'app/modules/wallforming/components/projects-manager/types/pendingLoadedProject';
import { ProjectsDialog } from 'app/modules/wallforming/components/projects-manager/types/projectDialog';
import { SavePayloadValidation } from 'app/modules/wallforming/components/projects-manager/types/savePayloadValidation';
import { mapArticleInstanceToPlacement } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { ProjectReference } from 'app/modules/wallforming/components/projects-manager/types/projectReference';

interface UseProjectSaveInput {
    resolvedActiveProject: ProjectReference | null;
    isDirty: boolean;
    currentProjectSignature: string;
    activeSessionId: string;
    pendingLoadedProject: PendingLoadedProject | null;
}

/**
 * Handles all save-related state and logic:
 * - Save-dialog field values (`saveName`, `saveDescription`, `saveError`).
 * - `validateSavePayload` — name/description length validation.
 * - `buildArticleGroups` — assembles the save payload, fetching representation IDs
 *   for each article that has instances placed in the scene.
 * - `executeSave` — core save operation, usable from any context (dialog, quick-save,
 *   autosave, or cross-hook via the orchestrator).
 * - `handleQuickSave` / `triggerSaveFlow` — smart dispatchers for keyboard shortcuts
 *   and the event bus.
 * - Autosave timer: fires `executeSave` after `AUTOSAVE_DELAY_MS` whenever the
 *   project is dirty and autosave is enabled.
 * - Syncs `isSaving` to the Zustand store so other components can subscribe directly.
 */
export const useProjectSave = ({
    resolvedActiveProject,
    isDirty,
    currentProjectSignature,
    activeSessionId,
    pendingLoadedProject
}: UseProjectSaveInput) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();

    const [saveName, setSaveName] = useState<string>(() => {
        const now = new Date();
        return `Project ${now.toLocaleDateString()}`;
    });
    const [saveDescription, setSaveDescription] = useState<string>('');
    const [saveError, setSaveError] = useState<string | null>(null);

    const {
        activeDialog,
        isAutosaveEnabled,
        articles,
        pois,
        setActiveProject,
        setLastSavedSignature,
        setIsSaving,
        setActiveDialog
    } = usePlacementStore(
        useShallow((state) => ({
            activeDialog: state.activeProjectDialog,
            isAutosaveEnabled: state.isAutosaveEnabled,
            articles: state.articles,
            pois: state.pois,
            setActiveProject: state.setActiveProject,
            setLastSavedSignature: state.setLastSavedSignature,
            setIsSaving: state.setIsSaving,
            setActiveDialog: state.setActiveProjectDialog
        }))
    );

    const saveProjectMutation = useSaveProject();
    const getMainRepresentationByArticleId = useGetMainRepresentationByArticleIdHook();

    // Sync isSaving to store so other components can subscribe directly
    useEffect(() => {
        setIsSaving(saveProjectMutation.isPending);
    }, [saveProjectMutation.isPending, setIsSaving]);

    // Persist autosave preference whenever it changes
    useEffect(() => {
        localStorage.setItem(AUTOSAVE_ENABLED_KEY, String(isAutosaveEnabled));
    }, [isAutosaveEnabled]);

    const validateSavePayload = useCallback(
        (name: string, description: string): SavePayloadValidation => {
            const trimmedName = name.trim();
            if (trimmedName.length === 0) {
                return { isValid: false, message: t('infoProjectsNameRequired') };
            }
            if (trimmedName.length > 200) {
                return { isValid: false, message: t('infoProjectsNameTooLong') };
            }
            if (description.length > 1000) {
                return { isValid: false, message: t('infoProjectsDescriptionTooLong') };
            }
            return { isValid: true, message: null };
        },
        [t]
    );

    const buildArticleGroups = useCallback(async () => {
        const articleIds = Object.keys(articles);
        const instanceToPoiGuids = new Map<string, Set<string>>();

        pois.forEach((poiData) => {
            const poiGuid = poiData.poi.poiGuid;
            Object.values(poiData.articles).forEach((instanceIds) => {
                instanceIds.forEach((instanceId) => {
                    const poiGuids = instanceToPoiGuids.get(instanceId) ?? new Set<string>();
                    poiGuids.add(poiGuid);
                    instanceToPoiGuids.set(instanceId, poiGuids);
                });
            });
        });

        const representationByArticleId = new Map<string, string>();

        for (const articleId of articleIds) {
            const articleInstances = articles[articleId]?.articleInstances ?? {};
            const hasInstances = Object.keys(articleInstances).length > 0;
            if (!hasInstances) {
                continue;
            }

            const representationResponse = await getMainRepresentationByArticleId(articleId, {
                layerLoadMode: GetMainRepresentationByArticleIdLayerLoadMode.None,
                includePrefabComponents: false
            });

            const representationId = representationResponse.data?.id;
            if (!representationId) {
                throw new Error(t('projectsRepresentationMissing'));
            }

            representationByArticleId.set(articleId, representationId);
        }

        return articleIds
            .map((articleId) => {
                const article = articles[articleId];
                const representationGuid = representationByArticleId.get(articleId);

                if (!article || !representationGuid) {
                    return null;
                }

                const instances = Object.entries(article.articleInstances)
                    .map(([instanceId, instance]) => {
                        const poiGuids = [...(instanceToPoiGuids.get(instanceId) ?? new Set(instance.poiGuids ?? []))];
                        if (poiGuids.length === 0) {
                            return null;
                        }
                        return mapArticleInstanceToPlacement(instanceId, instance, poiGuids, false);
                    })
                    .filter((item): item is NonNullable<typeof item> => item !== null);

                if (instances.length === 0) {
                    return null;
                }

                return {
                    articleGuid: articleId,
                    articleNumber: article.articleNumber,
                    lifeCycleCode: article.lifeCycleCode,
                    representationGuid,
                    instances
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    }, [articles, getMainRepresentationByArticleId, pois, t]);

    const openSaveDialog = useCallback(() => {
        const now = new Date();
        const defaultName = resolvedActiveProject?.name?.trim().length
            ? `${resolvedActiveProject.name} Copy`
            : `Project ${now.toLocaleDateString()}`;
        setSaveName(defaultName);
        setSaveDescription(resolvedActiveProject?.description?.trim() ?? '');
        setSaveError(null);
        setActiveDialog(ProjectsDialog.SAVE);
    }, [resolvedActiveProject, setActiveDialog]);

    const executeSave = useCallback(
        async ({
            projectId,
            name,
            description,
            successMessage,
            isAutosave = false,
            showToast = true
        }: {
            projectId?: string;
            name: string;
            description: string;
            successMessage?: string;
            isAutosave?: boolean;
            showToast?: boolean;
        }) => {
            const normalizedName = name.trim();
            const normalizedDescription = description.trim();

            const validation = validateSavePayload(normalizedName, normalizedDescription);
            if (!validation.isValid) {
                throw new Error(validation.message ?? t('errorSaveProject'));
            }

            const articleGroups = await buildArticleGroups();

            const response = await saveProjectMutation.mutateAsync({
                data: articleGroups,
                params: {
                    sessionId: activeSessionId,
                    projectId,
                    name: normalizedName,
                    description: normalizedDescription || undefined,
                    isAutosave
                }
            });

            if (!response.success) {
                throw new Error(response.errorMessage ?? t('errorSaveProject'));
            }

            const nextActiveProject: ProjectReference = {
                id: response.projectId,
                name: response.name,
                description: normalizedDescription || null
            };

            setActiveProject(nextActiveProject);
            setLastSavedSignature(currentProjectSignature);
            await queryClient.invalidateQueries({ queryKey: getGetProjectsQueryKey() });

            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('projectId', response.projectId);
            nextParams.set('formingsession', activeSessionId);
            window.history.replaceState(null, '', `${window.location.pathname}?${nextParams.toString()}`);

            if (showToast) {
                toastNotifications.success({
                    title: t('success'),
                    message: successMessage ?? t('infoSaveProjectSuccess', { projectName: response.name })
                });
            }
        },
        [
            validateSavePayload,
            buildArticleGroups,
            saveProjectMutation,
            activeSessionId,
            setActiveProject,
            setLastSavedSignature,
            currentProjectSignature,
            queryClient,
            searchParams,
            t
        ]
    );

    const handleSaveProject = useCallback(async () => {
        setSaveError(null);
        const validation = validateSavePayload(saveName, saveDescription);
        if (!validation.isValid) {
            setSaveError(validation.message);
            return;
        }

        try {
            await executeSave({
                projectId: undefined,
                name: saveName,
                description: saveDescription,
                successMessage: t('infoSaveProjectSuccess', { projectName: saveName })
            });
            setActiveDialog(ProjectsDialog.NONE);
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : t('projectsSaveFailed', 'Failed to save project.'));
        }
    }, [executeSave, saveDescription, saveName, setActiveDialog, t, validateSavePayload]);

    const handleQuickSave = useCallback(async () => {
        if (!resolvedActiveProject) {
            return;
        }

        setSaveError(null);
        try {
            await executeSave({
                projectId: resolvedActiveProject.id,
                name: resolvedActiveProject.name,
                description: resolvedActiveProject.description?.trim() ?? '',
                isAutosave: false,
                successMessage: t('infoSaveProjectSuccess', { projectName: resolvedActiveProject.name })
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : t('errorSaveProject');
            toastNotifications.error({ title: t('error'), message });
        }
    }, [executeSave, resolvedActiveProject, t]);

    // Autosave timer
    useEffect(() => {
        if (
            !isAutosaveEnabled ||
            !resolvedActiveProject ||
            !isDirty ||
            saveProjectMutation.isPending ||
            activeDialog === ProjectsDialog.SAVE ||
            pendingLoadedProject
        ) {
            return;
        }

        const timerId = window.setTimeout(() => {
            void executeSave({
                projectId: resolvedActiveProject.id,
                name: resolvedActiveProject.name,
                description: resolvedActiveProject.description?.trim() ?? '',
                isAutosave: true,
                showToast: false
            }).catch((error: unknown) => {
                const message = error instanceof Error ? error.message : t('errorSaveProject');
                toastNotifications.error({ title: t('error'), message });
            });
        }, AUTOSAVE_DELAY_MS);

        return () => window.clearTimeout(timerId);
    }, [
        executeSave,
        isAutosaveEnabled,
        isDirty,
        activeDialog,
        pendingLoadedProject,
        resolvedActiveProject,
        saveProjectMutation.isPending,
        t
    ]);

    const triggerSaveFlow = useCallback(() => {
        if (saveProjectMutation.isPending) {
            return;
        }

        if (activeDialog === ProjectsDialog.SAVE) {
            void handleSaveProject();
            return;
        }

        if (resolvedActiveProject) {
            if (!isDirty) {
                toastNotifications.success({
                    title: t('success', 'Success'),
                    message: t('infoProjectAlreadySaved')
                });
                return;
            }
            void handleQuickSave();
            return;
        }

        openSaveDialog();
    }, [
        handleQuickSave,
        handleSaveProject,
        isDirty,
        activeDialog,
        openSaveDialog,
        resolvedActiveProject,
        saveProjectMutation.isPending,
        t
    ]);

    return {
        saveName,
        setSaveName,
        saveDescription,
        setSaveDescription,
        saveError,
        isSavePending: saveProjectMutation.isPending,
        openSaveDialog,
        handleSaveProject,
        handleQuickSave,
        executeSave,
        triggerSaveFlow
    };
};
